import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SLOT_DEFS, type SlotState } from "@ranfac/shared";
import { fetchUploads } from "../api.js";
import { ProgressTracker } from "../components/ProgressTracker.js";
import { UploadSlot } from "../components/UploadSlot.js";

export function Sources() {
  const { period = "2026-01" } = useParams();
  const [slots, setSlots] = useState<SlotState[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchUploads(period)
      .then((r) => setSlots(r.slots))
      .catch((e) => setErr(String(e)));
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const slotByIdState = useMemo(() => {
    const m = new Map<string, SlotState>();
    for (const s of slots ?? []) m.set(s.slotId, s);
    return m;
  }, [slots]);

  const uploadsDone = (slots ?? []).filter((s) => s.status === "uploaded")
    .length;
  const uploadsTotal = SLOT_DEFS.length;

  const byPhase: Record<number, typeof SLOT_DEFS> = { 1: [], 2: [], 4: [], 5: [] };
  for (const d of SLOT_DEFS) byPhase[d.phase]!.push(d);

  return (
    <>
      <ProgressTracker
        period={period}
        uploadsDone={uploadsDone}
        uploadsTotal={uploadsTotal}
        reconsDone={0}
        reconsTotal={30}
        signaturesDone={0}
        signaturesTotal={30}
        binderLocked={false}
      />

      <div className="card">
        <div style={{ marginBottom: 8 }}>
          <Link to={`/close/${period}`}>&larr; back to close dashboard</Link>
        </div>
        <h1>Source Files</h1>
        <div className="subtle">
          Upload the Sage reports for the period. Each slot validates its file
          against the expected columns — a failed upload tells you exactly
          which column is off so you can re-export. You can replace a file at
          any time; prior versions are retained for audit.
        </div>
      </div>

      {err && <div className="banner warn">Could not load uploads: {err}</div>}

      {[1, 2, 4, 5].map((phase) => {
        const defs = byPhase[phase];
        if (!defs || defs.length === 0) return null;
        return (
          <div key={phase} className="card">
            <h2>
              {phaseLabel(phase)}{" "}
              <span className="subtle" style={{ fontWeight: 400 }}>
                — Phase {phase}
              </span>
            </h2>
            <div className="slot-grid">
              {defs.map((def) => (
                <UploadSlot
                  key={def.id}
                  period={period}
                  def={def}
                  state={
                    slotByIdState.get(def.id) ?? {
                      slotId: def.id,
                      status: "missing",
                      filename: null,
                      rowCount: null,
                      uploadedAt: null,
                      uploadedBy: null,
                      errors: [],
                    }
                  }
                  onChanged={load}
                />
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

function phaseLabel(phase: number): string {
  if (phase === 1) return "Week-1 prototype";
  if (phase === 2) return "MVP recons";
  if (phase === 4) return "Remaining recons";
  if (phase === 5) return "Binder & analytics";
  return `Phase ${phase}`;
}
